import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtISTDate, fmtISTTime } from "@/lib/ist";
import {
  CalendarDays, Clock, MonitorPlay, BadgeIndianRupee, Check, Target,
  Repeat, MessagesSquare, Send, Users, Star, ShieldCheck, UserRound,
} from "lucide-react";
import { RegisterForm } from "@/components/masterclass/RegisterForm";

// Placeholder presenter details, edit here once confirmed
const EVENT = {
  title: "Building a ₹1 Crore Pipeline",
  presenter: "Presenter to be announced",
  presenterRole: "Senior D32 India mentor",
};

const HERO_IMG = "https://images.unsplash.com/photo-1615791242458-caf8ff6245ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const scrollToForm = () => document.getElementById("register")?.scrollIntoView({ behavior: "smooth" });

function CTA({ testId, children, light }) {
  return (
    <button
      onClick={scrollToForm}
      data-testid={testId}
      className={`inline-flex items-center justify-center font-semibold px-7 py-3.5 rounded-full hover:-translate-y-0.5 transition-transform duration-300 ${
        light ? "bg-gold text-white hover:bg-[#5fc1f1]" : "bg-navy text-white hover:bg-navy-deep"
      }`}
    >
      {children}
    </button>
  );
}

function Hero({ sessions }) {
  const next = sessions?.find((s) => s.seats_left > 0);
  const more = sessions ? sessions.filter((s) => s.seats_left > 0).length - 1 : 0;
  const facts = [
    [CalendarDays, sessions === null ? "Loading dates…" : next ? `Next: ${fmtISTDate(next.starts_at)}` : "New dates coming soon", "masterclass-date"],
    [Clock, next ? `${fmtISTTime(next.starts_at)} IST${more > 0 ? ` · ${more} more date${more > 1 ? "s" : ""}` : ""}` : "Times shown in IST", "masterclass-time"],
    [MonitorPlay, "Live online, join from anywhere in India", "masterclass-venue"],
    [BadgeIndianRupee, "Free to attend", "masterclass-price"],
  ];
  return (
    <section className="bg-navy text-white pt-16 pb-20 lg:pt-24 lg:pb-28 overflow-hidden">
      <div className="container-narrow grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="overline text-gold mb-4">Free Online Masterclass</div>
          <h1 data-testid="masterclass-title" className="text-4xl sm:text-5xl lg:text-6xl font-display font-semibold leading-[1.05]">
            {EVENT.title}
          </h1>
          <p className="mt-6 max-w-xl text-lg text-gray-300">
            A practical, 90-minute live session for Indian founders and SME owners who want
            sales that arrive by design, not by luck.
          </p>
          <ul className="mt-8 grid sm:grid-cols-2 gap-3">
            {facts.map(([Icon, text, id]) => (
              <li key={id} data-testid={id} className="flex items-center gap-3 text-sm text-gray-200">
                <span className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-gold"><Icon size={16} /></span>
                {text}
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-wrap gap-4 items-center">
            <CTA testId="masterclass-hero-cta" light>Reserve my free seat</CTA>
            <span className="text-xs text-gray-400">Limited live seats per session</span>
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="aspect-[4/5] rounded-3xl bg-cover bg-center shadow-2xl" style={{ backgroundImage: `url(${HERO_IMG})` }} />
          <div className="absolute -bottom-6 -left-6 bg-white text-navy rounded-2xl p-5 shadow-xl max-w-[240px]">
            <div className="flex gap-0.5 text-gold">{[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}</div>
            <p className="mt-2 text-sm font-medium leading-snug">"The most useful 90 minutes I've spent on my sales process."</p>
            <div className="mt-2 text-xs text-gray-500">Karan Shah, Ahmedabad</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Problem() {
  const points = [
    "Most events collect visiting cards, not conversations that convert.",
    "Growth advice is loud on motivation and quiet on method.",
    "Referrals arrive randomly, so the next quarter is always a guess.",
  ];
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="container-narrow grid lg:grid-cols-2 gap-16">
        <div>
          <div className="overline text-gold mb-3">The Problem</div>
          <h2 className="text-3xl sm:text-4xl font-display font-semibold text-navy leading-tight">
            Why does business growth keep stalling?
          </h2>
        </div>
        <div>
          <ul className="space-y-5">
            {points.map((p) => (
              <li key={p} className="flex gap-4 text-gray-700">
                <span className="mt-2 w-2 h-2 rounded-full bg-gold flex-shrink-0" />{p}
              </li>
            ))}
          </ul>
          <p className="mt-8 text-gray-600 leading-relaxed">
            Indian business owners deserve more than handshakes. This masterclass shows you the
            exact structure D32 members use to turn their relationships into a reliable pipeline.
          </p>
        </div>
      </div>
    </section>
  );
}

function Learn() {
  const items = [
    [Target, "Spot hidden opportunities", "Find the high-value prospects already sitting in your network and phone book."],
    [Repeat, "Build a repeatable pipeline", "A simple weekly rhythm that doesn't depend on chance, charm or cold calls."],
    [MessagesSquare, "Convert without the hard sell", "Turn chai-meeting conversations into clients with a clear, respectful ask."],
    [Send, "Follow up like a pro", "Stop chasing. Use a light follow-up system that keeps deals moving."],
  ];
  return (
    <section className="py-20 lg:py-28 bg-cream">
      <div className="container-narrow">
        <div className="overline text-gold mb-3">What you'll learn</div>
        <h2 className="text-3xl sm:text-4xl font-display font-semibold text-navy max-w-2xl leading-tight">
          Four shifts that make growth predictable.
        </h2>
        <div className="mt-12 grid sm:grid-cols-2 gap-6">
          {items.map(([Icon, t, d], i) => (
            <div key={t} data-testid={`masterclass-learn-${i}`} className="bg-white rounded-2xl p-7 border border-gray-200 hover:-translate-y-1 hover:shadow-lg transition-transform duration-300">
              <div className="w-11 h-11 rounded-xl bg-navy text-gold flex items-center justify-center"><Icon size={18} /></div>
              <div className="mt-5 font-display text-xl text-navy">{t}</div>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const quotes = [
    ["We stopped hoping for referrals and started planning for them.", "Priya Sharma", "Lumen Digital · Chandigarh Tricity"],
    ["Clear, practical and very Indian in its examples. No fluff at all.", "Hardik Patel", "Patel Polymers · Ahmedabad"],
    ["My follow-ups finally have a system. Two deals closed within weeks.", "Vikram Singh", "Tricity Insurance Brokers"],
  ];
  return (
    <section className="py-20 lg:py-24 bg-navy text-white">
      <div className="container-narrow">
        <div className="overline text-gold mb-3">What members say</div>
        <h2 className="text-3xl sm:text-4xl font-display font-semibold max-w-2xl leading-tight">More than referrals. A system and a support crew.</h2>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {quotes.map(([q, n, r]) => (
            <div key={n} className="border border-white/15 rounded-2xl p-6 hover:border-gold/60 transition-colors">
              <div className="flex gap-0.5 text-gold">{[...Array(5)].map((_, i) => <Star key={i} size={13} fill="currentColor" />)}</div>
              <p className="mt-4 text-gray-200 text-sm leading-relaxed">"{q}"</p>
              <div className="mt-5 text-xs"><div className="font-semibold">{n}</div><div className="text-gray-400">{r}</div></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Outcomes() {
  const cards = [
    ["Sharper habits", "Clarity on where your next 10 clients will come from, and a weekly routine to reach them."],
    ["Your inner circle", "Introductions to business owners who understand what you do and who you serve."],
    ["A visible pipeline", "A one-page tracker so you can see, not guess, what's coming next month."],
  ];
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="container-narrow">
        <div className="overline text-gold mb-3">After the session</div>
        <h2 className="text-3xl sm:text-4xl font-display font-semibold text-navy max-w-2xl leading-tight">What changes in your next 30 days?</h2>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {cards.map(([t, d], i) => (
            <div key={t} className="relative rounded-2xl p-7 bg-cream border border-gray-200">
              <div className="text-xs uppercase tracking-widest text-gold font-semibold">Within 30 days</div>
              <div className="mt-3 font-display text-xl text-navy">{t}</div>
              <p className="mt-2 text-sm text-gray-600">{d}</p>
              <div className="absolute top-6 right-6 font-display text-4xl text-navy/10">0{i + 1}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Presenter() {
  return (
    <section className="py-20 bg-cream">
      <div className="container-narrow grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-4">
          <div data-testid="masterclass-presenter-photo" className="aspect-square rounded-3xl bg-navy/5 border border-dashed border-navy/30 flex items-center justify-center text-navy/40">
            <UserRound size={72} />
          </div>
        </div>
        <div className="md:col-span-8">
          <div className="overline text-gold mb-3">Your presenter</div>
          <h2 data-testid="masterclass-presenter-name" className="text-3xl font-display font-semibold text-navy">{EVENT.presenter}</h2>
          <div className="text-sm text-gray-500 mt-1">{EVENT.presenterRole}</div>
          <p className="mt-5 text-gray-600 leading-relaxed max-w-xl">
            Led by a D32 mentor who has helped hundreds of owners replace scattered networking
            with a steady, referral-driven pipeline. Full speaker profile coming soon.
          </p>
        </div>
      </div>
    </section>
  );
}

function Steps() {
  const steps = [
    ["Register free", "Save your seat with the form below in under a minute."],
    ["Get your link", "We email your online joining link and a short pre-work sheet."],
    ["Join live", "90 minutes of practical frameworks, examples and Q&A."],
    ["Put it to work", "Apply the pipeline plan and, if you like, visit a D32 Circle."],
  ];
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="container-narrow">
        <div className="overline text-gold mb-3">How it works</div>
        <h2 className="text-3xl sm:text-4xl font-display font-semibold text-navy leading-tight">Four simple steps.</h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map(([t, d], i) => (
            <div key={t} data-testid={`masterclass-step-${i + 1}`} className="border-t-2 border-gold pt-5">
              <div className="text-xs uppercase tracking-widest text-gray-500">Step {i + 1}</div>
              <div className="mt-2 font-display text-xl text-navy">{t}</div>
              <p className="mt-2 text-sm text-gray-600">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Register({ sessions }) {
  const perks = ["The full pipeline-building framework", "A one-page pipeline tracker template", "Live Q&A with a D32 mentor", "An invitation to visit a D32 Circle"];
  return (
    <section id="register" data-testid="masterclass-register-section" className="py-20 lg:py-28 bg-navy text-white scroll-mt-24">
      <div className="container-narrow grid lg:grid-cols-2 gap-16 items-start">
        <div>
          <div className="overline text-gold mb-3">Your next step</div>
          <h2 className="text-3xl sm:text-4xl font-display font-semibold leading-tight">Ready to stop guessing and start growing?</h2>
          <p className="mt-5 text-gray-300 max-w-md">Growth starts with one confident decision. Join us live, it costs nothing but 90 minutes.</p>
          <ul className="mt-8 space-y-4">
            {perks.map((p) => (
              <li key={p} className="flex items-center gap-3 text-gray-200">
                <span className="w-7 h-7 rounded-full bg-gold/20 text-gold flex items-center justify-center"><Check size={14} /></span>{p}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-cream text-gray-900 rounded-3xl p-8 lg:p-10">
          {sessions === null ? <div className="py-16 text-center text-gray-500">Loading sessions…</div> : <RegisterForm sessions={sessions} />}
        </div>
      </div>
    </section>
  );
}

function Guarantee() {
  return (
    <section className="py-20 bg-white">
      <div className="container-narrow max-w-3xl text-left">
        <ShieldCheck size={32} className="text-gold" />
        <h2 className="mt-4 text-3xl font-display font-semibold text-navy">What if it's not for you?</h2>
        <p className="mt-4 text-gray-600 leading-relaxed">
          No pitch-fest, no pressure. The masterclass is free and you can leave any time. If you
          want to see D32 in action afterwards, you're welcome to visit a Circle as our guest.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <CTA testId="masterclass-bottom-cta">Reserve my free seat</CTA>
          <Link to="/visit-a-circle" data-testid="masterclass-visit-link" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-navy text-navy font-semibold hover:bg-navy hover:text-white transition-colors">
            <Users size={16} /> Visit a Circle
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Masterclass() {
  const [sessions, setSessions] = useState(null);
  const [params] = useSearchParams();

  useEffect(() => {
    api.get("/masterclass/sessions").then((r) => setSessions(r.data)).catch(() => setSessions([]));
  }, []);

  useEffect(() => {
    if (params.get("join") === "pending") {
      setTimeout(() => toast.info("The joining link isn't ready yet. We'll email it to you before the session.", { id: "join-pending" }), 0);
    }
  }, [params]);

  return (
    <div data-testid="masterclass-page">
      <Hero sessions={sessions} />
      <Problem />
      <Learn />
      <Testimonials />
      <Outcomes />
      <Presenter />
      <Steps />
      <Register sessions={sessions} />
      <Guarantee />
    </div>
  );
}
